// Central analytics — wraps GA4 (gtag) and Meta Pixel (fbq).
// Tracking IDs are no longer hardcoded — they're fetched from
// /settings/tracking-ids (configurable in Admin → Analytics & SEO),
// with a localStorage fallback. See initTrackingConfig() below.

import { apiClient } from "./services/authService";

export let GA_ID = "";
export let META_PIXEL_ID = "";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?:  (...args: unknown[]) => void;
  }
}

export interface TrackingConfig {
  gaId?: string;
  metaPixelId?: string;
}

const STORAGE_KEY = "tracking_ids_config";
let gaScriptInjected = false;

/** Fetch tracking IDs (API first, then localStorage) and wire up gtag/fbq. Call once at app boot. */
export async function initTrackingConfig() {
  let config: TrackingConfig | null = null;
  try {
    const res = await apiClient.get<{ value: TrackingConfig }>("/settings/tracking-ids");
    if (res.data?.value) config = res.data.value;
  } catch {
    // backend may not have this key yet — fall through to localStorage
  }
  if (!config) {
    const local = localStorage.getItem(STORAGE_KEY);
    if (local) {
      try { config = JSON.parse(local); } catch {}
    }
  }
  if (!config) return;

  if (config.gaId?.trim()) {
    GA_ID = config.gaId.trim();
    if (!gaScriptInjected) {
      gaScriptInjected = true;
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(script);
    }
    window.gtag?.("config", GA_ID, { send_page_view: false });
  }

  if (config.metaPixelId?.trim()) {
    META_PIXEL_ID = config.metaPixelId.trim();
    window.fbq?.("init", META_PIXEL_ID);
    window.fbq?.("track", "PageView");
  }
}

// ── Page view (called automatically on route change) ──────────────────────────
export function trackPageView(path: string) {
  if (GA_ID) window.gtag?.("config", GA_ID, { page_path: path });
  if (META_PIXEL_ID) window.fbq?.("track", "PageView");
}

// ── Custom GA4 event helper ───────────────────────────────────────────────────
export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>
) {
  if (!GA_ID) return;
  window.gtag?.("event", name, params);
}

// ── Key conversion events ─────────────────────────────────────────────────────

/** User clicks "Subscribe" / "Secure My Spot" on pricing page */
export function trackPlanSelected(planName: string, price: number) {
  trackEvent("plan_selected", { plan_name: planName, value: price, currency: "USD" });
  if (META_PIXEL_ID) window.fbq?.("track", "InitiateCheckout", { content_name: planName, value: price, currency: "USD" });
}

/** User completes signup */
export function trackSignupComplete(method: string = "email") {
  trackEvent("sign_up", { method });
  if (META_PIXEL_ID) window.fbq?.("track", "CompleteRegistration");
}

/** User completes signup via a referral link */
export function trackReferralSignup(refCode: string) {
  trackEvent("referral_signup", { referral_code: refCode });
  if (META_PIXEL_ID) window.fbq?.("track", "CompleteRegistration", { referral_code: refCode });
}

/** User completes payment / subscription */
export function trackPurchase(planName: string, value: number) {
  trackEvent("purchase", { plan_name: planName, value, currency: "USD", transaction_id: Date.now().toString() });
  if (META_PIXEL_ID) window.fbq?.("track", "Purchase", { value, currency: "USD" });
}

/** User shares any public page */
export function trackShare(contentType: "quiz" | "countdown" | "tip" | "poll" | "case" | "leaderboard" | "qa", title: string) {
  trackEvent("share", { content_type: contentType, item_id: title });
  if (META_PIXEL_ID) window.fbq?.("track", "Share", { content_type: contentType });
}

/** User clicks WhatsApp Notify Me on countdown page */
export function trackWhatsAppLead(courseTitle: string) {
  trackEvent("generate_lead", { source: "whatsapp_notify", course: courseTitle });
  if (META_PIXEL_ID) window.fbq?.("track", "Lead", { content_name: courseTitle });
}

/** User finishes a quiz */
export function trackQuizComplete(quizTitle: string, score: number, total: number) {
  trackEvent("quiz_complete", { quiz_title: quizTitle, score, total, pct: Math.round((score / total) * 100) });
}

/** User views a countdown page — measures top-of-funnel */
export function trackCountdownView(courseTitle: string) {
  trackEvent("countdown_view", { course: courseTitle });
  if (META_PIXEL_ID) window.fbq?.("track", "ViewContent", { content_name: courseTitle, content_type: "course" });
}
